import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Printer, Search, FileText, Calendar, AlertTriangle, Download, CheckCircle } from 'lucide-react';
import { getChecksReport, toggleCheckDeposited, exportChecksExcel, toggleCheckInvoice } from '../api';
import { toast } from '../hooks/use-toast';
import { DateRangePresets } from '../components/DateRangePresets';

const ChecksReport = () => {
  const navigate = useNavigate();
  const [checks, setChecks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    loadChecks();
  }, []);

  const loadChecks = async () => {
    try {
      const data = await getChecksReport();
      setChecks(data);
    } catch (error) {
      console.error('Error loading checks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleDeposited = async (check, idx) => {
    const newDeposited = !check.deposited;
    try {
      await toggleCheckDeposited({
        sourceId: check.sourceId,
        paymentId: check.paymentId,
        sourceType: check.sourceType,
        deposited: newDeposited
      });
      setChecks(prev => prev.map((c, i) => i === idx ? { ...c, deposited: newDeposited } : c));
      toast({ title: newDeposited ? 'הופקד' : 'בוטל', description: `שיק #${check.checkNumber} ${newDeposited ? 'סומן כהופקד' : 'סומן כממתין'}` });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לעדכן', variant: 'destructive' });
    }
  };

  const handleToggleInvoice = async (check, idx) => {
    const newHasInvoice = !check.hasInvoice;
    try {
      await toggleCheckInvoice({
        sourceId: check.sourceId,
        paymentId: check.paymentId,
        sourceType: check.sourceType,
        hasInvoice: newHasInvoice
      });
      setChecks(prev => prev.map((c, i) => i === idx ? { ...c, hasInvoice: newHasInvoice } : c));
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לעדכן', variant: 'destructive' });
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const getCheckStatus = (checkDate) => {
    if (!checkDate) return 'unknown';
    if (checkDate < today) return 'past';
    if (checkDate === today) return 'today';
    return 'future';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0;
    return `₪${num.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Apply search first (used both for the cards and the table)
  const searchFiltered = checks.filter(c => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (c.customerName || '').toLowerCase().includes(q) ||
           (c.bankName || '').toLowerCase().includes(q) ||
           (c.checkNumber || '').includes(q);
  });

  // Cards reflect all checks (respecting search) regardless of the chosen view
  const inRange = filterStatus === 'range' && (dateFrom || dateTo)
    ? searchFiltered.filter(c => c.checkDate && (!dateFrom || c.checkDate >= dateFrom) && (!dateTo || c.checkDate <= dateTo))
    : searchFiltered;
  const pastChecks = inRange.filter(c => getCheckStatus(c.checkDate) === 'past');
  const futureChecks = inRange.filter(c => getCheckStatus(c.checkDate) !== 'past');

  // Table stays empty until the user picks an option from the list
  const filtered = (() => {
    if (!filterStatus) return [];
    if (filterStatus === 'past') return searchFiltered.filter(c => getCheckStatus(c.checkDate) === 'past');
    if (filterStatus === 'future') return searchFiltered.filter(c => getCheckStatus(c.checkDate) !== 'past');
    if (filterStatus === 'range') {
      if (!dateFrom && !dateTo) return [];
      return searchFiltered.filter(c => c.checkDate &&
        (!dateFrom || c.checkDate >= dateFrom) &&
        (!dateTo || c.checkDate <= dateTo));
    }
    return searchFiltered; // 'all'
  })();

  const totalAmount = filtered.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען דוח שיקים...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 px-4 print:bg-white print:py-2" dir="rtl">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center print:hidden">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">דוח שיקים</h1>
            <p className="text-gray-600">{checks.length} שיקים במערכת</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => exportChecksExcel()} className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
              <Download className="ml-2 w-5 h-5" />
              ייצוא Excel
            </Button>
            <Button onClick={() => window.print()} className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">
              <Printer className="ml-2 w-5 h-5" />
              הדפסה
            </Button>
            <Button onClick={() => navigate('/reports')} variant="outline" className="border-2 hover:bg-gray-50">
              <ArrowRight className="ml-2 w-5 h-5" />
              חזור לדווח
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6 print:hidden">
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="py-4 text-center">
              <p className="text-sm text-blue-700 font-semibold">סה&quot;כ שיקים</p>
              <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalAmount)}</p>
              <p className="text-xs text-blue-600">{filtered.length} שיקים</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-orange-200 bg-orange-50 cursor-pointer hover:bg-orange-100 transition-colors" onClick={() => setFilterStatus(filterStatus === 'past' ? 'all' : 'past')}>
            <CardContent className="py-4 text-center">
              <p className="text-sm text-orange-700 font-semibold">שיקים שעברו</p>
              <p className="text-2xl font-bold text-orange-900">{formatCurrency(pastChecks.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0))}</p>
              <p className="text-xs text-orange-600">{pastChecks.length} שיקים</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-200 bg-green-50 cursor-pointer hover:bg-green-100 transition-colors" onClick={() => setFilterStatus(filterStatus === 'future' ? 'all' : 'future')}>
            <CardContent className="py-4 text-center">
              <p className="text-sm text-green-700 font-semibold">שיקים עתידיים</p>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(futureChecks.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0))}</p>
              <p className="text-xs text-green-600">{futureChecks.length} שיקים</p>
            </CardContent>
          </Card>
        </div>

        {/* Search & Filter */}
        <div className="mb-6 flex gap-4 print:hidden">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="חיפוש לפי שם לקוח, בנק או מספר שיק..."
              className="w-full pr-10 pl-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors bg-white"
              data-testid="checks-search"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 border-2 border-gray-300 rounded-lg bg-white text-sm font-semibold"
            data-testid="checks-filter"
          >
            <option value="">בחר אפשרות...</option>
            <option value="all">הכל</option>
            <option value="past">שיקים שעברו</option>
            <option value="future">שיקים עתידיים</option>
            <option value="range">חיפוש בין 2 תאריכים</option>
          </select>
        </div>

        <div className="print:hidden">
          <DateRangePresets
            startDate={filterStatus === 'range' ? dateFrom : ''}
            endDate={filterStatus === 'range' ? dateTo : ''}
            onChange={(from, to) => { setFilterStatus('range'); setDateFrom(from); setDateTo(to); }}
          />
        </div>

        {/* Date Range inputs - shown only when "between 2 dates" is selected */}
        {filterStatus === 'range' && (
          <div className="mb-6 flex flex-wrap items-end gap-4 bg-white p-4 rounded-lg border-2 border-indigo-200 print:hidden" data-testid="checks-date-range">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">מתאריך</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg bg-white text-sm"
                data-testid="checks-date-from"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">עד תאריך</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg bg-white text-sm"
                data-testid="checks-date-to"
              />
            </div>
            {(dateFrom || dateTo) && (
              <Button
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                variant="outline"
                className="border-2 hover:bg-gray-50"
                data-testid="checks-date-clear"
              >
                נקה תאריכים
              </Button>
            )}
          </div>
        )}

        {/* Print Header */}
        <div className="hidden print:block mb-4 text-center">
          <h1 className="text-2xl font-bold">דוח שיקים</h1>
          <p className="text-sm text-gray-600">{new Date().toLocaleDateString('he-IL')}</p>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-16 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500">
                {!filterStatus
                  ? 'בחר אפשרות מהרשימה כדי להציג שיקים'
                  : checks.length === 0
                    ? 'אין שיקים במערכת'
                    : filterStatus === 'range' && !dateFrom && !dateTo
                      ? 'בחר טווח תאריכים כדי להציג שיקים'
                      : 'לא נמצאו שיקים התואמים לבחירה'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto border rounded-lg shadow-lg bg-white">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
                  <th className="p-3 text-right font-bold">#</th>
                  <th className="p-3 text-right font-bold">תאריך פירעון</th>
                  <th className="p-3 text-right font-bold">שם לקוח</th>
                  <th className="p-3 text-right font-bold">סכום</th>
                  <th className="p-3 text-right font-bold">שם בנק</th>
                  <th className="p-3 text-right font-bold">סניף</th>
                  <th className="p-3 text-right font-bold">מס&apos; חשבון</th>
                  <th className="p-3 text-right font-bold">מס&apos; שיק</th>
                  <th className="p-3 text-right font-bold">הופקד ל</th>
                  <th className="p-3 text-center font-bold">יש חשבונית</th>
                  <th className="p-3 text-center font-bold">סטטוס</th>
                  <th className="p-3 text-center font-bold print:hidden">הפקדה</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((check, idx) => {
                  const status = getCheckStatus(check.checkDate);
                  const origIdx = checks.indexOf(check);
                  return (
                    <tr key={idx} className={`${check.deposited ? 'bg-green-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors ${status === 'past' && !check.deposited ? 'opacity-70' : ''}`}>
                      <td className="p-3 border-b text-gray-500">{idx + 1}</td>
                      <td className="p-3 border-b font-medium">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDate(check.checkDate)}
                        </div>
                      </td>
                      <td className="p-3 border-b font-semibold text-gray-900">
                        {check.customerName}
                        <span className={`text-[10px] mr-2 px-1.5 py-0.5 rounded ${check.sourceType === 'פרויקט' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {check.sourceType}
                        </span>
                      </td>
                      <td className="p-3 border-b font-bold text-green-700">{formatCurrency(check.amount)}</td>
                      <td className="p-3 border-b text-gray-700">{check.bankName || '-'}</td>
                      <td className="p-3 border-b text-gray-700">{check.branch || '-'}</td>
                      <td className="p-3 border-b text-gray-700">{check.accountNumber || '-'}</td>
                      <td className="p-3 border-b text-gray-700">{check.checkNumber || '-'}</td>
                      <td className="p-3 border-b text-gray-700">{check.depositedTo || '-'}</td>
                      <td className="p-3 border-b text-center">
                        <button
                          onClick={() => handleToggleInvoice(check, origIdx)}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all mx-auto ${
                            check.hasInvoice
                              ? 'bg-green-500 border-green-500 text-white'
                              : 'border-gray-300 hover:border-green-400 text-transparent hover:text-green-300'
                          }`}
                          title={check.hasInvoice ? 'יש חשבונית - לחץ לבטל' : 'אין חשבונית - לחץ לסמן'}
                          data-testid={`toggle-invoice-${idx}`}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      </td>
                      <td className="p-3 border-b text-center">
                        {check.deposited ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-200 text-green-800 font-semibold flex items-center gap-1 justify-center">
                            <CheckCircle className="w-3 h-3" /> הופקד
                          </span>
                        ) : status === 'past' ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-600 font-semibold">עבר</span>
                        ) : status === 'today' ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 font-semibold flex items-center gap-1 justify-center">
                            <AlertTriangle className="w-3 h-3" /> היום
                          </span>
                        ) : status === 'future' ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">ממתין</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-semibold">ללא תאריך</span>
                        )}
                      </td>
                      <td className="p-3 border-b text-center print:hidden">
                        <button
                          onClick={() => handleToggleDeposited(check, origIdx)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${check.deposited ? 'bg-green-100 text-green-700 hover:bg-red-50 hover:text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-700'}`}
                          data-testid={`toggle-deposited-${idx}`}
                        >
                          {check.deposited ? 'בטל הפקדה' : 'סמן כהופקד'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-bold">
                  <td colSpan={3} className="p-3 text-right border-t-2">סה&quot;כ {filtered.length} שיקים</td>
                  <td className="p-3 border-t-2 text-green-700">{formatCurrency(totalAmount)}</td>
                  <td colSpan={8} className="p-3 border-t-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChecksReport;
